import { useState } from 'react';
import moment from 'moment-timezone';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { getCompanyCountry, getCurrentUserId } from '@/utils/auth';
import { formatDistance, haversineMeters } from '@/utils/distance';
import { getGeolocation } from '@/utils/geolocation';
import { hasNumericValue, hasValue } from '@/utils/validation';

/**
 * Shows the verification state of a call's location under the Call Location field and lets an
 * officer on the call confirm an approximate or not-found location from the device's GPS.
 * Anyone who can edit the call may remove a confirmation or stop a learned location being used
 * (forgetting only returns future calls to the geocoder). State is read from `callCard` on every
 * render so realtime updates and address edits are reflected immediately.
 */

interface OnSceneLocationControlProps {
  callCard: any;
  /** False for closed calls. */
  callOpen: boolean;
  /** MDT officer assigned to the call. */
  canConfirm: boolean;
  /** Passes the call's edit gate in CAD or MDT. */
  canEdit?: boolean;
}

const GPS_ACCURACY_LIMIT_M = 25;
// The first high-accuracy fix is often coarse; keep sampling this long for one within the limit.
const GPS_SAMPLING_WINDOW_MS = 15000;
const GPS_ATTEMPT_TIMEOUT_MS = 10000;
// getGeolocation has no timeout on the native-app bridge, so every attempt carries its own guard.
// It fires before getGeolocation's browser guard, which reports a hang as PERMISSION_DENIED.
const GPS_GUARD_SLACK_MS = 1000;
const GPS_RETRY_DELAY_MS = 1000;
const DISTANCE_SANITY_M = 3000;
const REFRESH_GUARD_MS = 10000;
const PERMISSION_DENIED = 1;

type Fix = { latitude: number; longitude: number; accuracy: number };
type LocateResult = { fix: Fix } | { errorCode: number };

function locateOnce(timeoutMs: number): Promise<LocateResult> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (result: LocateResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(guard);
      resolve(result);
    };
    const guard = setTimeout(() => settle({ errorCode: 3 }), timeoutMs + GPS_GUARD_SLACK_MS);
    getGeolocation(
      (pos) => {
        const { latitude, longitude, accuracy } = pos?.coords ?? ({} as any);
        if (hasNumericValue(latitude) && hasNumericValue(longitude)) {
          settle({ fix: { latitude, longitude, accuracy: hasNumericValue(accuracy) ? accuracy : Infinity } });
        } else {
          settle({ errorCode: 2 });
        }
      },
      (err) => settle({ errorCode: err?.code ?? 2 }),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}

/** Best fix within the sampling window, stopping at the first one within the accuracy limit. */
async function locateBest(): Promise<LocateResult> {
  const started = Date.now();
  let best: Fix | null = null;
  let attemptTimeout = GPS_ATTEMPT_TIMEOUT_MS;
  for (;;) {
    const result = await locateOnce(attemptTimeout);
    if ('errorCode' in result) {
      return best ? { fix: best } : result;
    }
    if (!best || result.fix.accuracy < best.accuracy) {
      best = result.fix;
    }
    const remaining = GPS_SAMPLING_WINDOW_MS - (Date.now() - started);
    if (best.accuracy <= GPS_ACCURACY_LIMIT_M || remaining <= GPS_RETRY_DELAY_MS) {
      return { fix: best };
    }
    await new Promise((r) => setTimeout(r, GPS_RETRY_DELAY_MS));
    attemptTimeout = Math.max(remaining - GPS_RETRY_DELAY_MS, 2000);
  }
}

export function OnSceneLocationControl({ callCard, callOpen, canConfirm, canEdit = false }: OnSceneLocationControlProps) {
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  // Re-renders after this control mutates the shared callCard object.
  const [, setRenderTick] = useState(0);

  if (!hasValue(callCard?.id)) {
    return null;
  }

  const status: string | null = callCard.addressVerificationStatus ?? null;
  const country = getCompanyCountry();
  const rerender = () => setRenderTick((t) => t + 1);

  const onNetworkFailure = (message: string) => (result: any) => {
    setBusy(false);
    // A server response already produced its own message via ajaxCall.
    if (result == null) {
      App.showPopupNotification(message, 'error');
    }
  };

  const refreshFromServer = () => {
    const guard = setTimeout(() => setBusy(false), REFRESH_GUARD_MS);
    api.getCallCardById(callCard.id, false, (result: any) => {
      clearTimeout(guard);
      if (result) {
        callCard.latitude = result.latitude;
        callCard.longitude = result.longitude;
        callCard.addressVerificationStatus = result.addressVerificationStatus ?? null;
        callCard.locationPinAccuracyM = result.locationPinAccuracyM ?? null;
        callCard.addressVerifiedBy = result.addressVerifiedBy ?? null;
        callCard.addressVerifiedByName = result.addressVerifiedByName ?? null;
        callCard.addressVerifiedAt = result.addressVerifiedAt ?? null;
        callCard.learnedLocation = result.learnedLocation ?? null;
        callCard.learnedAddressId = result.learnedAddressId ?? result.learnedLocation?.learnedAddressId ?? null;
      }
      setBusy(false);
      rerender();
    });
  };

  const confirmLocation = async () => {
    setBusy(true);
    setLocating(true);
    const located = await locateBest();
    setLocating(false);

    if ('errorCode' in located) {
      setBusy(false);
      App.showPopupNotification(located.errorCode === PERMISSION_DENIED
        ? 'Location access is off for Orna. Turn it on in your device settings, then try again.'
        : 'Could not get a GPS position. Try again in a moment.', 'error');
      return;
    }
    const { latitude, longitude, accuracy } = located.fix;
    if (accuracy > GPS_ACCURACY_LIMIT_M) {
      setBusy(false);
      const shown = Number.isFinite(accuracy) ? `±${formatDistance(accuracy, country)}` : 'unknown';
      App.showPopupNotification(
        `GPS accuracy is ${shown}, which is not precise enough to confirm. Move to an open area and try again.`, 'warning');
      return;
    }
    // An AREA pin is the service-area centre, so distance from it says nothing about the address.
    if (status === 'LOW' && hasNumericValue(callCard.latitude) && hasNumericValue(callCard.longitude)) {
      const meters = haversineMeters(latitude, longitude, callCard.latitude, callCard.longitude);
      if (meters > DISTANCE_SANITY_M) {
        const ok = await (window as any).kstConfirm({
          title: 'Confirm location',
          message: `Your position is ${formatDistance(meters, country)} from this call's pin. Confirm you are at the address?`,
          confirmText: "I'm at the address",
          confirmClass: 'primary',
        });
        if (!ok) {
          setBusy(false);
          return;
        }
      }
    }
    api.confirmCallCardLocationPin(callCard.id, latitude, longitude, accuracy,
      () => {
        callCard.addressVerificationStatus = 'OFFICER_ON_SCENE';
        callCard.latitude = latitude;
        callCard.longitude = longitude;
        callCard.locationPinAccuracyM = accuracy;
        callCard.addressVerifiedBy = getCurrentUserId();
        callCard.addressVerifiedAt = new Date().toISOString();
        rerender();
        App.showPopupNotification('Location confirmed.', 'success');
        // The chip names the confirmer as the server recorded them.
        refreshFromServer();
      },
      onNetworkFailure('The location could not be confirmed. Check your connection and try again.'));
  };

  const removeConfirmation = () => {
    setBusy(true);
    api.undoCallCardLocationPin(callCard.id,
      () => {
        App.showPopupNotification('Confirmation removed.', 'success');
        refreshFromServer();
      },
      onNetworkFailure('The confirmation could not be removed. Check your connection and try again.'));
  };

  const forgetLearnedLocation = async () => {
    const ok = await (window as any).kstConfirm({
      title: 'Stop using this location?',
      message: 'New calls to this address will use the map service again, and this call will be marked as an approximate location.',
      confirmText: 'Stop using it',
      confirmClass: 'primary',
    });
    if (!ok) {
      return;
    }
    setBusy(true);
    api.forgetCallCardLearnedLocation(callCard.id,
      () => {
        App.showPopupNotification('This location will no longer be used.', 'success');
        refreshFromServer();
      },
      onNetworkFailure('The location could not be updated. Check your connection and try again.'));
  };

  if (status === 'OFFICER_ON_SCENE') {
    const accuracyM = callCard.locationPinAccuracyM;
    const name = typeof callCard.addressVerifiedByName === 'string' ? callCard.addressVerifiedByName.trim() : '';
    const confirmedAt = hasValue(callCard.addressVerifiedAt) ? moment(callCard.addressVerifiedAt) : null;
    const parts = [name ? `Confirmed on scene by ${name}` : 'Confirmed on scene'];
    if (confirmedAt?.isValid()) {
      parts.push(confirmedAt.format('h:mm A'));
    }
    if (hasNumericValue(accuracyM)) {
      parts.push(`GPS ±${formatDistance(accuracyM, country)}`);
    }
    return (
      <div>
        <span className="onscene-chip onscene-status--confirmed">
          &#10003; {parts.join(' · ')}
        </span>
        {callOpen && canEdit && (
          <button type="button" className="label-link onscene-control__remove" disabled={busy} onClick={removeConfirmation}>
            Remove confirmation
          </button>
        )}
      </div>
    );
  }

  const learned = callCard.learnedLocation;
  if (learned && status !== 'LOW' && status !== 'AREA') {
    const by = typeof learned.capturedByName === 'string' ? learned.capturedByName.trim() : '';
    const capturedAt = hasValue(learned.capturedAt) ? moment(learned.capturedAt) : null;
    const when = capturedAt?.isValid() ? capturedAt.format('MMM D') : '';
    let line = 'Location from an on-scene confirmation';
    if (by) line += ` by ${by}`;
    if (when) line += `, ${when}`;
    return (
      <div>
        <span className="onscene-control__hint onscene-control__provenance">{line}</span>
        {callOpen && canEdit && (
          <button type="button" className="label-link onscene-control__remove" disabled={busy} onClick={forgetLearnedLocation}>
            Don't use this location again
          </button>
        )}
      </div>
    );
  }

  if (status !== 'LOW' && status !== 'AREA') {
    return null;
  }

  const isArea = status === 'AREA';
  return (
    <div>
      <span className={`onscene-chip ${isArea ? 'onscene-status--area' : 'onscene-status--approx'}`}>
        &asymp; {isArea ? 'Address not found' : 'Approximate location'}
      </span>
      {isArea && (
        <span className="onscene-control__hint">The pin marks the service area, not the address.</span>
      )}
      {/* Every uncertain call is confirmable; the server alone decides whether the spot is
          also learned (docs/GEOCODING_ON_SCENE_TRUTH_PLAN.md, "Confirm vs learn"). */}
      {canConfirm && callOpen && (
        <button type="button" className="kstc-btn kstc-btn--primary onscene-control__confirm" disabled={busy}
                onClick={confirmLocation}>
          {locating ? 'Getting your position…' : <><i className="fas fa-map-marker-alt"></i> Confirm location</>}
        </button>
      )}
    </div>
  );
}
